import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, UserRound } from "lucide-react";
import { Reveal, Overline } from "@/components/landing/primitives";
import { IMG } from "@/content/site";

function Parallax({ src, alt, className, gray }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} className={`absolute inset-0 w-full h-[112%] object-cover ${gray ? "grayscale-[20%]" : ""}`} />
      <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
    </div>
  );
}

const FOCUS = ["Laser ablativi e non ablativi", "Laser vascolari", "Pigmentazioni e tatuaggi", "Cicatrici", "Resurfacing", "Fotoinvecchiamento", "Pori e texture", "Lesioni cutanee selezionate", "Skin Quality", "PRP e polinucleotidi", "Radiofrequenza e ultrasuoni", "Tricologia e diradamento"];
const APPROACH = ["Pelle", "Cicatrici", "Texture", "Luminosità", "Discromie", "Lesioni vascolari", "Compattezza", "Qualità dei capelli"];

export function DoctorPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-canvas text-ink">
      <section className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><Link to="/" className="inline-flex items-center gap-2 text-sm text-muted hover:text-ink link-underline mb-10"><ArrowLeft className="w-4 h-4" /> Home</Link></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <Parallax src={IMG.doctor} alt="Dott. Alessandro Valle" className="h-[60vh] lg:h-[84vh] w-full" gray />
              <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-muted">Dott. Alessandro Valle · Medico</p>
            </div>
            <div className="lg:col-span-7">
              <Reveal><Overline>Il medico</Overline></Reveal>
              <Reveal delay={0.1} as="h1" className="mt-6 font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">Dott. Alessandro <span className="italic text-sagedark">Valle</span></Reveal>
              <Reveal delay={0.15} as="p" className="mt-6 text-lg text-sagedark font-serif italic">Medico focalizzato su trattamenti laser, Skin Quality e Tricologia.</Reveal>
              {[
                "Si è laureato in Medicina e Chirurgia presso l’Università Vita-Salute San Raffaele di Milano. Durante il percorso formativo ha maturato esperienza clinica e di ricerca nell’ambito della Dermatologia presso l’IRCCS Ospedale San Raffaele e dell’Immunologia presso l’Ospedale Niguarda di Milano.",
                "L’esperienza ospedaliera ha contribuito a sviluppare una conoscenza approfondita della pelle, delle sue patologie e dei meccanismi biologici che regolano infiammazione, danno e riparazione tissutale.",
                "Ha scelto di concentrare la propria attività sulla medicina estetica tecnologica: fisica dei laser, interazione tra energia e tessuti, selezione delle lunghezze d’onda, gestione dei parametri, endpoint clinici e sicurezza. Prosegue la formazione presso la Scuola di Medicina Estetica Agorà di Milano ed è socio della Società Italiana di Medicina Estetica. Ha inoltre partecipato ad attività formative presso la sede Fotona di Lubiana.",
              ].map((p, i) => (
                <Reveal key={i} delay={0.2 + i * 0.06} as="p" className="mt-5 text-base md:text-lg text-muted leading-relaxed max-w-2xl">{p}</Reveal>
              ))}

              <Reveal delay={0.2} as="p" className="mt-10 text-[11px] uppercase tracking-[0.2em] text-muted">La sua attività si concentra su</Reveal>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                {FOCUS.map((f, i) => (
                  <Reveal key={f} delay={i * 0.03} className="flex items-center gap-3 border-t border-ink/10 py-3 text-[15px] text-ink/80">
                    <span className="text-sage font-serif text-sm">{String(i + 1).padStart(2, "0")}</span>{f}
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="bg-graphite text-canvas p-8 md:p-14">
            <p className="text-[11px] uppercase tracking-[0.2em] text-sage">Approccio non volumetrico</p>
            <p className="mt-5 text-lg md:text-xl leading-relaxed text-canvas/85 max-w-3xl">
              L’attività non è costruita sulla trasformazione sistematica dei lineamenti attraverso
              filler volumetrici, ma sul miglioramento progressivo della pelle e dei tessuti. Gli
              iniettivi vengono impiegati principalmente con finalità di idratazione, Skin Quality,
              biostimolazione e rigenerazione. L’obiettivo è preservare un volto riconoscibile,
              naturale ed espressivo.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {APPROACH.map((t) => <span key={t} className="border border-canvas/25 px-4 py-2 text-[13px] text-canvas/70">{t}</span>)}
            </div>
            <p className="mt-10 font-serif italic text-2xl md:text-3xl text-canvas max-w-3xl">
              La tecnologia è efficace solo quando si comprende quale tessuto trattare e quale risposta biologica ottenere.
            </p>
          </div>
          <Reveal delay={0.1} as="div" className="mt-12 text-center">
            <button onClick={() => navigate("/contatti")} className="group relative inline-flex items-center overflow-hidden border border-ink px-10 py-4 text-xs uppercase tracking-[0.2em]">
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota una valutazione</span>
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const DOCTOR = ["Valutazione medica", "Trattamenti laser", "Skin Quality", "Tricologia", "Procedure rigenerative", "Protocolli tecnologici avanzati"];
const TEAM = ["Corpo", "Cellulite", "Edema e microcircolo", "Lassità", "Rimodellamento", "Qualità dei tessuti", "Supporto domiciliare", "Follow-up dei programmi"];

export function StaffPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-canvas text-ink">
      <section className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal><Link to="/" className="inline-flex items-center gap-2 text-sm text-muted hover:text-ink link-underline mb-10"><ArrowLeft className="w-4 h-4" /> Home</Link></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <Reveal><Overline>Studio e Staff</Overline></Reveal>
              <Reveal delay={0.1} as="h1" className="mt-7 font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">Competenze differenti, un unico percorso.</Reveal>
            </div>
            <Reveal delay={0.2} as="p" className="lg:col-span-5 text-lg text-muted leading-relaxed">
              La struttura nasce dalla collaborazione tra professionisti con competenze differenti.
              Ogni procedura viene affidata al professionista più appropriato e inserita all’interno
              di un percorso condiviso.
            </Reveal>
          </div>
        </div>
      </section>
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Parallax src={IMG.staff} alt="Studio a Genova" className="h-[38vh] md:h-[54vh] w-full" />
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
          <Reveal className="bg-canvas p-8 md:p-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted">Dott. Alessandro Valle</p>
            <div className="mt-6 flex flex-wrap gap-2.5">{DOCTOR.map((t) => <span key={t} className="border border-ink/15 px-4 py-2 text-[13px] text-ink/70">{t}</span>)}</div>
          </Reveal>
          <Reveal delay={0.08} className="bg-canvas p-8 md:p-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted">Staff dedicato</p>
            <div className="mt-6 flex flex-wrap gap-2.5">{TEAM.map((t) => <span key={t} className="border border-ink/15 px-4 py-2 text-[13px] text-ink/70">{t}</span>)}</div>
          </Reveal>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16">
          <Overline>Il team</Overline>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="group">
              <div className="aspect-[3/4] overflow-hidden bg-sand ring-1 ring-inset ring-ink/10">
                <img src={IMG.doctor} alt="Dott. Alessandro Valle" className="w-full h-full object-cover grayscale-[20%]" />
              </div>
              <p className="mt-4 font-serif text-2xl">Dott. Alessandro Valle</p>
              <p className="mt-1 text-sm text-muted">Medico · Laser, Skin Quality, Tricologia</p>
              <p className="mt-2 text-sm text-muted leading-relaxed">Valutazione medica, trattamenti laser, lesioni cutanee, cicatrici e procedure rigenerative.</p>
            </div>
            {[1, 2].map((i) => (
              <div key={i}>
                <div className="aspect-[3/4] bg-stone ring-1 ring-inset ring-ink/10 flex flex-col items-center justify-center text-muted">
                  <UserRound className="w-9 h-9 opacity-40" strokeWidth={1.2} />
                  <p className="mt-4 text-xs uppercase tracking-[0.2em]">Foto in arrivo</p>
                </div>
                <p className="mt-4 font-serif text-2xl text-ink/40">Professionista dello staff</p>
                <p className="mt-1 text-sm text-muted">Percorsi corpo · nome e presentazione in arrivo</p>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 text-center">
          <button onClick={() => navigate("/contatti")} className="group relative inline-flex items-center overflow-hidden border border-ink px-10 py-4 text-xs uppercase tracking-[0.2em]">
            <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota una valutazione</span>
          </button>
        </div>
      </section>
    </div>
  );
}
